import { withTranslation } from '@application/i18n/with_translation';
import { ActionCard, Button, Flex, type IconType, isIconType, Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { type ClientConditionApiModel, ReceptionConditionInfoScreenFlow } from '@visiba/api-vcs-client-v2';

export type ConditionInfoScreenFlow = ReceptionConditionInfoScreenFlow;

export type ConditionWithInfoScreen = ClientConditionApiModel;

interface ConditionInfoScreenProps {
	condition: ConditionWithInfoScreen;
	flow: ConditionInfoScreenFlow;
	onBack: () => void;
	onCancel: () => void;
}

export function shouldShowConditionInfoScreen(condition: ConditionWithInfoScreen, flow: ConditionInfoScreenFlow): boolean {
	return condition.InfoScreen?.Flows.includes(flow) === true;
}

export function ConditionInfoScreen({ condition, flow, onBack, onCancel }: ConditionInfoScreenProps): JSX.Element | null {
	const infoScreen = condition.InfoScreen;

	if (!infoScreen?.Flows.includes(flow)) {
		return null;
	}

	return (
		<Flex fluid='vertical' gap={tokens.spacing.x6} grow='1'>
			<Flex gap={tokens.spacing.x1_5} grow='1'>
				<TopBar.Content
					goBack
					goBackIntercept={(event) => {
						event.preventDefault();
						onBack();
					}}
					onAbort={onCancel}
				/>
				<Title title={condition.Name ?? ''} titleSize='small' description={infoScreen.Description ?? undefined} />
				<Spacer vertical={tokens.spacing.x0_5} />
				<Flex gap={tokens.spacing.x1}>
					{infoScreen.Buttons.map((button) => {
						const description = getButtonDescription(button.Description, button.Url);
						const safeUrl = getSafeNavigationUrl(button.Url);

						if (!safeUrl) return null;

						return (
							<ActionCard.Link
								key={button.Id}
								href={safeUrl}
								target='_blank'
								rel='noopener noreferrer'
								icon={getKnownIcon(button.Icon)}
								trailingIcon='linkOut'
							>
								<ActionCard.Title>{button.Title}</ActionCard.Title>
								{description ? <Text.p variant='bodyLargeRegular'>{description}</Text.p> : null}
							</ActionCard.Link>
						);
					})}
				</Flex>
			</Flex>
			<Flex grow='1' justify='flex-end' align='center' gap={tokens.spacing.x1}>
				<Text.p variant='bodyRegular'>{withTranslation('Component.ConditionInfoScreen.NotFoundText')}</Text.p>
				<Button variant='ghost' onClick={onBack}>
					{withTranslation('Component.ConditionInfoScreen.SelectAnotherReasonButton')}
				</Button>
			</Flex>
		</Flex>
	);
}

function getKnownIcon(icon: string): IconType {
	return isIconType(icon) ? icon : 'infoCircle';
}

function getSafeNavigationUrl(url?: string | null): string | null {
	if (!url) {
		return null;
	}

	try {
		const parsedUrl = new URL(url, window.location.origin);
		if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
			return parsedUrl.toString();
		}
	} catch {
		return null;
	}

	return null;
}

function getButtonDescription(description?: string | null, url?: string | null): string | null {
	if (description?.trim()) {
		return description;
	}

	if (!url) {
		return null;
	}

	try {
		return new URL(url).hostname;
	} catch {
		return null;
	}
}
